import {
  DEFAULT_CURRENCY_CODE,
  getCurrencyMinorDigits,
  getCurrencyMultiplier,
  normalizeCurrencyCode,
  type CurrencyCode
} from '@klear/constants/currency'
import groupBy from 'lodash/groupBy'
import isInteger from 'lodash/isInteger'
import mapValues from 'lodash/mapValues'
import sortBy from 'lodash/sortBy'
import times from 'lodash/times'
import toNumber from 'lodash/toNumber'
import toPairs from 'lodash/toPairs'

export function moneyToJson(money: Money): string {
  return encodeMoneyWire(money.amount, money.currency)
}
export type MoneyJson = string

export function encodeMoneyWire(amount: bigint, currency: CurrencyCode): string {
  const digits = getCurrencyMinorDigits(currency)
  const negative = amount < 0n
  const minor = (negative ? -amount : amount).toString().padStart(digits + 1, '0')
  const whole = digits === 0 ? minor : minor.slice(0, -digits)
  const fraction = digits === 0 ? '' : `.${minor.slice(-digits)}`
  return `${currency} ${negative ? '-' : ''}${whole}${fraction}`
}

export const MONEY_WIRE_PATTERN = /^([A-Za-z]{3}) *(-?)(\d+)(?:\.(\d+))?$/

export function decodeMoneyWire(text: string): { amount: bigint; currency: CurrencyCode } | undefined {
  const match = MONEY_WIRE_PATTERN.exec(text.trim())
  if (match == null) return undefined
  const currency = normalizeCurrencyCode(match[1])
  if (currency == null) return undefined
  const fraction = match[4] ?? ''
  if (fraction.length !== getCurrencyMinorDigits(currency)) return undefined
  return { amount: BigInt(`${match[2]}${match[3]}${fraction}`), currency }
}

export function currenciesFromMonies(monies: readonly Money[]): CurrencyCode[] {
  return [...new Set(monies.filter((m) => !m.isZero()).map((m) => m.currency))].sort()
}

export function sumByCurrency(monies: readonly Money[]): Map<CurrencyCode, Money> {
  return new Map(
    toPairs(mapValues(groupBy(monies, 'currency'), (bucket) => Money.sum(bucket))) as [CurrencyCode, Money][]
  )
}

export function perCurrencyTotals(monies: readonly Money[]): Money[] {
  return sortBy([...sumByCurrency(monies).values()], (money) => money.currency)
}

export function formatPerCurrencyTotals(totals: readonly Money[], emptyLabel = '—'): string {
  if (totals.length === 0) return emptyLabel
  return totals.map((money) => money.toString()).join(' + ')
}

export function dominantCurrencyTotal(totals: Map<CurrencyCode, Money>): Money {
  const preferred = totals.get(DEFAULT_CURRENCY_CODE)
  if (preferred != null) return preferred
  const buckets = [...totals.values()]
  if (buckets.length === 0) return new Money()
  return buckets.reduce((a, b) => (a.amount >= b.amount ? a : b))
}

export function assertSingleCurrency(monies: readonly Money[], fallback?: CurrencyCode): CurrencyCode {
  const currencies = currenciesFromMonies(monies)
  if (currencies.length === 1) return currencies[0]!
  if (fallback != null) return fallback
  throw new Error('No single currency found')
}

export interface MoneyFormatOptions {
  notation?: 'standard' | 'compact' | 'auto'
  decimals?: 'auto' | 'none'
  style?: 'currency' | 'plain'
}

const ABBREVIATE_FROM_MAJOR_UNITS = 1000n

export function divideRoundedMinor(numerator: bigint, denominator: bigint): bigint {
  if (denominator === 0n) throw new Error('Cannot divide minor units by zero')
  const shifted = 2n * numerator + denominator
  const divisor = 2n * denominator
  const quotient = shifted / divisor
  const exact = shifted % divisor === 0n
  const negative = shifted < 0n !== divisor < 0n
  return !exact && negative ? quotient - 1n : quotient
}

type DecimalStringFormatter = { format(value: string): string }

function currencyFormatterForDecimalStrings(options: Intl.NumberFormatOptions): DecimalStringFormatter {
  return new Intl.NumberFormat('en-US', options) as unknown as DecimalStringFormatter
}

export function formatScaledDecimal(scaled: bigint, digits: number): string {
  const negative = scaled < 0n
  const magnitude = (negative ? -scaled : scaled).toString().padStart(digits + 1, '0')
  const whole = magnitude.slice(0, magnitude.length - digits)
  const fraction = digits === 0 ? '' : `.${magnitude.slice(magnitude.length - digits)}`
  return `${negative ? '-' : ''}${whole}${fraction}`
}

export class Money {
  static readonly __klearClass = 'Money'

  amount: bigint
  currency: CurrencyCode

  constructor(instance?: MoneyJson | Money) {
    if (instance == null) {
      this.amount = 0n
      this.currency = DEFAULT_CURRENCY_CODE
      return
    }
    if (instance instanceof Money) {
      this.amount = instance.amount
      this.currency = instance.currency
      return
    }
    const decoded = decodeMoneyWire(instance)
    if (decoded == null) throw new Error(`Invalid Money wire: ${instance}`)
    this.amount = decoded.amount
    this.currency = decoded.currency
  }

  static fromMinorUnits(amount: bigint, currency: CurrencyCode): Money {
    const money = new Money()
    money.amount = amount
    money.currency = currency
    return money
  }

  static zero(currency: CurrencyCode = DEFAULT_CURRENCY_CODE): Money {
    const m = new Money()
    m.currency = currency
    return m
  }

  get zero() {
    return Money.zero(this.currency)
  }

  get smallestUnit(): Money {
    const unit = new Money()
    unit.amount = 1n
    unit.currency = this.currency
    return unit
  }

  static sum(monies: readonly Money[]) {
    if (monies.length === 0) return new Money()
    const target = monies.find((m) => !m.isZero())?.currency ?? monies[0]!.currency
    const excluded = monies.filter((m) => !m.isZero() && m.currency !== target)
    if (excluded.length > 0) {
      console.warn(
        `Money.sum: ${excluded.length} amount(s) not in ${target} excluded from total`,
        excluded.map((m) => m.toJSON())
      )
    }
    const included = monies.filter((m) => m.isZero() || m.currency === target)
    const total = included.slice(1).reduce((acc, m) => acc.add(m), new Money(included[0]!))
    total.currency = target
    return total
  }

  private warnOnCurrencyMismatch(operation: string, other: Money): void {
    if (this.currency !== other.currency && !this.isZero() && !other.isZero()) {
      console.warn(
        `Money.${operation}: currency mismatch ${this.currency} vs ${other.currency}; using ${this.currency}`
      )
    }
  }

  add(other: Money) {
    this.warnOnCurrencyMismatch('add', other)
    const m = new Money(this)
    m.currency = this.isZero() ? other.currency : this.currency
    m.amount = this.amount + other.amount
    return m
  }

  subtract(other: Money) {
    this.warnOnCurrencyMismatch('subtract', other)
    const m = new Money(this)
    m.currency = this.isZero() ? other.currency : this.currency
    m.amount = this.amount - other.amount
    return m
  }

  mul(factor: bigint): Money {
    const m = new Money(this)
    m.amount = this.amount * factor
    return m
  }

  allocate(parts: number): Money[] {
    if (!isInteger(parts) || parts < 1) {
      throw new Error(`Money.allocate: parts must be a positive integer, got ${parts}`)
    }
    const divisor = BigInt(parts)
    const share = this.amount / divisor
    const step = this.amount < 0n ? -1n : 1n
    let remainder = this.amount - share * divisor
    return times(parts, () => {
      const allocated = new Money(this)
      allocated.amount = share
      if (remainder !== 0n) {
        allocated.amount += step
        remainder -= step
      }
      return allocated
    })
  }

  prorate(weights: readonly Money[]): Money[] {
    if (weights.length === 0) return []
    const totalWeight = Money.sum(weights)
    const shares: Money[] = []
    let allocated = this.zero
    weights.forEach((weight, index) => {
      if (index === weights.length - 1) {
        shares.push(this.subtract(allocated))
        return
      }
      const share = new Money(this)
      share.amount = totalWeight.isPositive()
        ? divideRoundedMinor(this.amount * weight.amount, totalWeight.amount)
        : divideRoundedMinor(this.amount, BigInt(weights.length))
      allocated = allocated.add(share)
      shares.push(share)
    })
    return shares
  }

  compare(other: Money): number {
    this.warnOnCurrencyMismatch('compare', other)
    if (this.amount < other.amount) return -1
    if (this.amount > other.amount) return 1
    return 0
  }

  greaterThan(other: Money): boolean {
    return this.compare(other) > 0
  }

  greaterThanOrEqualTo(other: Money): boolean {
    return this.compare(other) >= 0
  }

  lessThan(other: Money): boolean {
    return this.compare(other) < 0
  }

  lessThanOrEqualTo(other: Money): boolean {
    return this.compare(other) <= 0
  }

  equals(other: Money): boolean {
    if (this.isZero() && other.isZero()) return true
    return this.currency === other.currency && this.amount === other.amount
  }

  static min(...monies: Money[]): Money {
    if (monies.length === 0) return new Money()
    return monies.slice(1).reduce((acc, m) => (m.lessThan(acc) ? m : acc), monies[0]!)
  }

  static max(...monies: Money[]): Money {
    if (monies.length === 0) return new Money()
    return monies.slice(1).reduce((acc, m) => (m.greaterThan(acc) ? m : acc), monies[0]!)
  }

  toJSON() {
    return moneyToJson(this)
  }

  isZero(): boolean {
    return this.amount === 0n
  }

  isPositive(): boolean {
    return this.amount > 0n
  }

  isNegative(): boolean {
    return this.amount < 0n
  }

  private denomination(): bigint {
    return BigInt(getCurrencyMultiplier(this.currency))
  }

  isWholeDenomination(): boolean {
    return this.amount % this.denomination() === 0n
  }

  toMajorUnits(): number {
    return toNumber((this.amount / this.denomination()).toString())
  }

  #autoNotation(): 'standard' | 'compact' {
    const magnitude = this.amount < 0n ? -this.amount : this.amount
    return magnitude >= ABBREVIATE_FROM_MAJOR_UNITS * this.denomination() ? 'compact' : 'standard'
  }

  toString(options: MoneyFormatOptions = {}): string {
    const { notation: requested = 'standard', decimals = 'auto', style = 'currency' } = options
    const currencyDigits = getCurrencyMinorDigits(this.currency)
    const fractionDigits = decimals === 'none' ? 0 : currencyDigits
    const notation = requested === 'auto' ? this.#autoNotation() : requested

    if (style === 'plain') {
      const rescaled =
        fractionDigits === currencyDigits
          ? this.amount
          : divideRoundedMinor(this.amount * 10n ** BigInt(fractionDigits), 10n ** BigInt(currencyDigits))
      return formatScaledDecimal(rescaled, fractionDigits)
    }

    return currencyFormatterForDecimalStrings({
      style: 'currency',
      currency: this.currency,
      notation,
      minimumFractionDigits: notation === 'compact' ? 0 : fractionDigits,
      maximumFractionDigits: notation === 'compact' ? 1 : fractionDigits
    }).format(formatScaledDecimal(this.amount, currencyDigits))
  }
}
